import React, { useEffect, useMemo, useRef, useState } from 'react';
import { X, List, LayoutGrid, Search, ZoomIn, Share2, ChevronUp, ChevronDown, Download } from 'lucide-react';
import { toast } from 'sonner';
import { pageText } from '../../data/sampleAnnualReport';

const ZOOMS = [100, 150, 200];

const Highlight = ({ text, query }) => {
  if (!query) return <>{text}</>;
  const parts = String(text).split(new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'));
  return (
    <>
      {parts.map((part, i) =>
        part.toLowerCase() === query.toLowerCase()
          ? <mark key={i} className="bg-amber-300 text-[#0A1628] rounded px-0.5">{part}</mark>
          : part
      )}
    </>
  );
};

const PageBlocks = ({ page, report, query }) => (
  <>
    {page.blocks.map((b, i) => {
      if (b.type === 'cover') {
        return (
          <div key={i} className="h-full flex flex-col items-center justify-center text-center px-8 py-16">
            <img src={`${process.env.REACT_APP_BACKEND_URL}/api/uploads/ewrf_47years_logo.jpg`} alt="EWRF" className="h-24 w-auto mb-6" />
            <div className="text-3xl font-black text-[#0A1628] font-serif"><Highlight text={b.heading} query={query} /></div>
            <div className="text-xl text-[#1565C0] font-bold mt-2"><Highlight text={b.sub} query={query} /></div>
            <div className="mt-6 h-0.5 w-24 bg-gradient-to-r from-[#1565C0] to-[#D4AF37]" />
            <div className="text-xs text-gray-500 mt-6 tracking-wide"><Highlight text={b.note} query={query} /></div>
          </div>
        );
      }
      if (b.type === 'h') {
        return (
          <h3 key={i} className="text-xl font-black text-[#0A1628] font-serif border-b-2 border-[#D4AF37] pb-2 mb-4">
            <Highlight text={b.text} query={query} />
          </h3>
        );
      }
      if (b.type === 'p') {
        return <p key={i} className="text-sm text-gray-700 leading-relaxed mb-3"><Highlight text={b.text} query={query} /></p>;
      }
      if (b.type === 'toc') {
        return (
          <ol key={i} className="space-y-2 text-sm">
            {report.pages.filter((p) => p.num > 2 && p.title !== 'Back Cover').map((p) => (
              <li key={p.num} className="flex items-baseline gap-2">
                <span className="text-gray-700 font-semibold">{p.title}</span>
                <span className="flex-1 border-b border-dotted border-gray-300" />
                <span className="font-mono text-gray-500">{p.num}</span>
              </li>
            ))}
          </ol>
        );
      }
      if (b.type === 'stats') {
        return (
          <div key={i} className="grid grid-cols-2 gap-3 my-4">
            {b.items.map((s) => (
              <div key={s.label} className="bg-blue-50 rounded-xl p-4 text-center">
                <div className="text-2xl font-black text-[#1565C0]"><Highlight text={s.value} query={query} /></div>
                <div className="text-[11px] uppercase tracking-widest text-gray-500 font-bold mt-1"><Highlight text={s.label} query={query} /></div>
              </div>
            ))}
          </div>
        );
      }
      if (b.type === 'table') {
        return (
          <table key={i} className="w-full text-sm my-4">
            <tbody>
              {b.rows.map((r) => (
                <tr key={r[0]} className="border-b border-gray-100">
                  <td className="py-2 text-gray-600"><Highlight text={r[0]} query={query} /></td>
                  <td className="py-2 text-right font-bold text-[#0A1628]"><Highlight text={r[1]} query={query} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        );
      }
      return null;
    })}
  </>
);

export const ReportReader = ({ report, initialPage = 1, onClose }) => {
  const [zoom, setZoom] = useState(100);
  const [panel, setPanel] = useState(null); // 'contents' | 'thumbs' | 'search'
  const [query, setQuery] = useState('');
  const [activeQuery, setActiveQuery] = useState('');
  const [current, setCurrent] = useState(initialPage);
  const scrollRef = useRef(null);
  const pageRefs = useRef({});

  const matches = useMemo(() => {
    if (!activeQuery) return [];
    const q = activeQuery.toLowerCase();
    return report.pages
      .filter((p) => pageText(p).toLowerCase().includes(q))
      .map((p) => ({ num: p.num, title: p.title, snippet: pageText(p) }));
  }, [activeQuery, report]);

  const goTo = (num, smooth = true) => {
    const el = pageRefs.current[num];
    if (el) el.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'start' });
  };

  useEffect(() => {
    const t = setTimeout(() => goTo(initialPage, false), 60);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    window.history.replaceState(null, '', `#report=${report.id}&page=${current}`);
  }, [current, report.id]);

  const onScroll = () => {
    const c = scrollRef.current;
    if (!c) return;
    const mid = c.scrollTop + c.clientHeight / 3;
    let best = 1;
    for (const p of report.pages) {
      const el = pageRefs.current[p.num];
      if (el && el.offsetTop <= mid) best = p.num;
    }
    setCurrent(best);
  };

  const share = async () => {
    const url = `${window.location.origin}/annual-reports#report=${report.id}&page=${current}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied', { description: `Opens ${report.label} at page ${current}` });
    } catch {
      window.prompt('Copy this link:', url);
    }
  };

  const sections = useMemo(() => {
    const seen = new Set();
    return report.pages.filter((p) => {
      if (seen.has(p.section)) return false;
      seen.add(p.section);
      return true;
    });
  }, [report]);

  const pageW = Math.round(680 * (zoom / 100));

  return (
    <div className="fixed inset-0 z-[100] bg-[#0A1628] flex flex-col" data-testid="report-reader">
      {/* Toolbar */}
      <div className="flex items-center gap-1.5 md:gap-3 px-3 md:px-5 py-2.5 bg-[#0D2444] text-white shadow-lg flex-wrap">
        <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10" data-testid="reader-close-btn" aria-label="Close reader">
          <X className="w-5 h-5" />
        </button>
        <div className="font-serif font-bold text-sm md:text-base mr-1">Annual Report {report.label}</div>
        <div className="text-xs bg-white/10 rounded-full px-3 py-1 font-mono" data-testid="reader-page-counter">
          Page {current} of {report.pages.length}
        </div>
        <div className="flex-1" />
        <button onClick={() => setPanel(panel === 'contents' ? null : 'contents')}
          className={`p-2 rounded-lg hover:bg-white/10 ${panel === 'contents' ? 'bg-white/15' : ''}`}
          title="Contents" data-testid="reader-contents-btn"><List className="w-5 h-5" /></button>
        <button onClick={() => setPanel(panel === 'thumbs' ? null : 'thumbs')}
          className={`p-2 rounded-lg hover:bg-white/10 ${panel === 'thumbs' ? 'bg-white/15' : ''}`}
          title="Page thumbnails" data-testid="reader-thumbs-btn"><LayoutGrid className="w-5 h-5" /></button>
        <button onClick={() => setPanel(panel === 'search' ? null : 'search')}
          className={`p-2 rounded-lg hover:bg-white/10 ${panel === 'search' ? 'bg-white/15' : ''}`}
          title="Search this report" data-testid="reader-search-btn"><Search className="w-5 h-5" /></button>
        <div className="flex items-center gap-1 bg-white/10 rounded-full px-1 py-0.5" data-testid="reader-zoom-group">
          <ZoomIn className="w-4 h-4 ml-1.5 opacity-70" />
          {ZOOMS.map((z) => (
            <button key={z} onClick={() => setZoom(z)}
              className={`text-xs font-bold rounded-full px-2.5 py-1 ${zoom === z ? 'bg-[#D4AF37] text-[#0A1628]' : 'hover:bg-white/10'}`}
              data-testid={`reader-zoom-${z}`}>{z}%</button>
          ))}
        </div>
        <button onClick={share} className="p-2 rounded-lg hover:bg-white/10" title="Share this page" data-testid="reader-share-btn">
          <Share2 className="w-5 h-5" />
        </button>
        <a href={report.href} target="_blank" rel="noreferrer noopener"
          className="hidden md:inline-flex items-center gap-1.5 bg-[#D4AF37] text-[#0A1628] font-bold text-xs rounded-full px-3.5 py-1.5 hover:brightness-110"
          data-testid="reader-download-btn">
          <Download className="w-3.5 h-3.5" /> PDF · {report.size}
        </a>
      </div>

      <div className="flex flex-1 min-h-0">
        {/* Side panel */}
        {panel && (
          <div className="w-72 md:w-80 bg-[#0F2440] text-white overflow-y-auto border-r border-white/10 flex-shrink-0" data-testid="reader-side-panel">
            {panel === 'contents' && (
              <div className="p-4">
                <div className="text-[10px] uppercase tracking-widest text-[#D4AF37] font-black mb-3">Contents</div>
                {sections.map((s) => (
                  <button key={s.section} onClick={() => goTo(s.num)}
                    className={`w-full text-left px-3 py-2.5 rounded-lg text-sm hover:bg-white/10 flex justify-between gap-2 ${current >= s.num ? 'text-white' : 'text-white/70'}`}
                    data-testid={`toc-${s.num}`}>
                    <span>{s.section}</span>
                    <span className="font-mono text-white/50">{s.num}</span>
                  </button>
                ))}
              </div>
            )}
            {panel === 'thumbs' && (
              <div className="p-4 grid grid-cols-2 gap-3">
                {report.pages.map((p) => (
                  <button key={p.num} onClick={() => goTo(p.num)}
                    className={`rounded-lg overflow-hidden text-left border-2 ${current === p.num ? 'border-[#D4AF37]' : 'border-transparent'}`}
                    data-testid={`thumb-${p.num}`}>
                    <div className="bg-white aspect-[1/1.35] p-2 flex flex-col">
                      <div className="text-[8px] font-black text-[#0A1628] leading-tight">{p.title}</div>
                      <div className="mt-1 space-y-0.5 flex-1">
                        {[...Array(6)].map((_, i) => <div key={i} className="h-0.5 bg-gray-200 rounded" style={{ width: `${90 - i * 9}%` }} />)}
                      </div>
                    </div>
                    <div className="text-center text-[10px] py-1 text-white/70 font-mono">{p.num}</div>
                  </button>
                ))}
              </div>
            )}
            {panel === 'search' && (
              <div className="p-4">
                <div className="text-[10px] uppercase tracking-widest text-[#D4AF37] font-black mb-3">Search this report</div>
                <form onSubmit={(e) => { e.preventDefault(); setActiveQuery(query.trim()); }} className="flex gap-2">
                  <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="e.g. volunteers"
                    className="flex-1 bg-white/10 rounded-lg px-3 py-2 text-sm placeholder-white/40 outline-none focus:ring-2 ring-[#D4AF37]"
                    data-testid="reader-search-input" />
                  <button type="submit" className="bg-[#D4AF37] text-[#0A1628] rounded-lg px-3 text-sm font-bold" data-testid="reader-search-submit">Go</button>
                </form>
                {activeQuery && (
                  <div className="mt-4 space-y-2" data-testid="reader-search-results">
                    <div className="text-xs text-white/60">{matches.length} matching page{matches.length === 1 ? '' : 's'}</div>
                    {matches.map((m) => {
                      const idx = m.snippet.toLowerCase().indexOf(activeQuery.toLowerCase());
                      const snip = m.snippet.slice(Math.max(0, idx - 30), idx + 50);
                      return (
                        <button key={m.num} onClick={() => goTo(m.num)}
                          className="w-full text-left bg-white/5 hover:bg-white/10 rounded-lg p-3"
                          data-testid={`search-result-${m.num}`}>
                          <div className="text-xs font-bold text-[#D4AF37]">Page {m.num} — {m.title}</div>
                          <div className="text-[11px] text-white/70 mt-1">…{snip}…</div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Pages */}
        <div ref={scrollRef} onScroll={onScroll} className="flex-1 overflow-y-auto py-6 px-3" data-testid="reader-pages-scroll">
          <div className="flex flex-col items-center gap-6">
            {report.pages.map((p) => {
              const highlighted = activeQuery && matches.some((m) => m.num === p.num);
              return (
                <div key={p.num} ref={(el) => { pageRefs.current[p.num] = el; }}
                  className={`bg-white rounded-sm shadow-2xl relative transition-all ${highlighted ? 'ring-4 ring-[#D4AF37]' : ''}`}
                  style={{ width: `${pageW}px`, maxWidth: '96vw', minHeight: `${Math.round(pageW * 1.35)}px` }}
                  data-testid={`report-page-${p.num}`}>
                  <div className="p-8 md:p-10 h-full">
                    <PageBlocks page={p} report={report} query={activeQuery} />
                  </div>
                  <div className="absolute bottom-3 inset-x-0 text-center text-[10px] text-gray-400 font-mono">
                    EWRF Annual Report {report.label} · Page {p.num}
                  </div>
                </div>
              );
            })}
            <div className="text-white/40 text-xs pb-8">Sample pages for the online reader — download the PDF for the official report.</div>
          </div>
        </div>
      </div>

      {/* Prev / next floating */}
      <div className="absolute right-4 bottom-4 flex flex-col gap-2">
        <button onClick={() => goTo(Math.max(1, current - 1))} className="bg-white/10 hover:bg-white/20 text-white rounded-full p-2.5 backdrop-blur" data-testid="reader-prev-btn" aria-label="Previous page">
          <ChevronUp className="w-5 h-5" />
        </button>
        <button onClick={() => goTo(Math.min(report.pages.length, current + 1))} className="bg-white/10 hover:bg-white/20 text-white rounded-full p-2.5 backdrop-blur" data-testid="reader-next-btn" aria-label="Next page">
          <ChevronDown className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

export default ReportReader;
